import { useState } from 'react';
import { ActionIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { IoCheckmark, IoClose } from 'react-icons/io5';
import { Button, Modal, Textarea, Tooltip } from '@shared/ui';
import {
  sellerApplicationsModerationApi,
  type SellerProfileChangeField,
} from '@modules/seller-applications/api/seller-applications-moderation.api';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import { formatDate } from '@shared/utils';
import { ZoomableImage } from './ZoomableImage';

type ProfileValues = {
  shopName: string | null;
  activityDescription: string | null;
  logoFileUrl: string | null;
};

type Props = {
  applicationId: number;
  current: ProfileValues;
  pending: ProfileValues;
  submittedAt: string | null;
  onModerated: () => void;
};

const FIELD_LABELS: Record<SellerProfileChangeField, string> = {
  shopName: 'Название',
  activityDescription: 'Описание',
  logo: 'Логотип',
};

function errorMessage(e: unknown): string {
  return (
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    String(e)
  );
}

function LogoThumb({ url }: { url: string | null }) {
  if (!url?.trim()) {
    return <span className="seller-profile-changes__empty">Нет логотипа</span>;
  }
  return (
    <ZoomableImage
      src={devProxyS3Url(url) || url}
      alt="Логотип"
      className="seller-profile-changes__logo"
    />
  );
}

function TextValue({ value }: { value: string | null }) {
  const text = value?.trim();
  return text ? (
    <span className="seller-profile-changes__text">{text}</span>
  ) : (
    <span className="seller-profile-changes__empty">Не указано</span>
  );
}

export function SellerProfileChangesSection({
  applicationId,
  current,
  pending,
  submittedAt,
  onModerated,
}: Props) {
  const [rejectField, setRejectField] = useState<SellerProfileChangeField | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fields: SellerProfileChangeField[] = [];
  if (pending.logoFileUrl != null) fields.push('logo');
  if (pending.shopName != null) fields.push('shopName');
  if (pending.activityDescription != null) fields.push('activityDescription');

  const approveMut = useMutation({
    mutationFn: (field: SellerProfileChangeField) =>
      sellerApplicationsModerationApi.approveProfileChanges(applicationId, [field]),
    onSuccess: (_data, field) => {
      onModerated();
      notifications.show({
        title: `${FIELD_LABELS[field]}: одобрено`,
        message: 'Новое значение уже на витрине.',
        color: 'green',
      });
    },
    onError: (e) => {
      notifications.show({ title: 'Ошибка', message: errorMessage(e), color: 'red' });
    },
  });

  const rejectMut = useMutation({
    mutationFn: ({ field, reason }: { field: SellerProfileChangeField; reason: string }) =>
      sellerApplicationsModerationApi.rejectProfileChanges(applicationId, {
        reason,
        fields: [field],
      }),
    onSuccess: (_data, { field }) => {
      onModerated();
      setRejectField(null);
      setRejectReason('');
      notifications.show({
        title: `${FIELD_LABELS[field]}: отклонено`,
        message: 'На витрине осталось прежнее значение.',
        color: 'orange',
      });
    },
    onError: (e) => {
      notifications.show({ title: 'Ошибка', message: errorMessage(e), color: 'red' });
    },
  });

  if (fields.length === 0) return null;

  const busy = approveMut.isPending || rejectMut.isPending;

  const renderValue = (field: SellerProfileChangeField, source: ProfileValues) =>
    field === 'logo' ? (
      <LogoThumb url={source.logoFileUrl} />
    ) : (
      <TextValue value={field === 'shopName' ? source.shopName : source.activityDescription} />
    );

  return (
    <section className="seller-profile-changes">
      <div className="seller-profile-changes__head">
        <span className="seller-profile-changes__dot" aria-hidden />
        <h3 className="seller-profile-changes__title">Изменения витрины на модерации</h3>
        {submittedAt ? (
          <span className="seller-profile-changes__meta">с {formatDate(submittedAt)}</span>
        ) : null}
      </div>
      <p className="seller-profile-changes__hint">
        Покупатели видят прежние данные, пока изменения не одобрены. Остальное в магазине повторно
        не модерируется.
      </p>

      <ul className="seller-profile-changes__list">
        {fields.map((field) => (
          <li key={field} className="seller-profile-changes__item">
            <span className="seller-profile-changes__label">{FIELD_LABELS[field]}</span>
            <div className="seller-profile-changes__side seller-profile-changes__side--current">
              <span className="seller-profile-changes__side-label">Сейчас</span>
              {renderValue(field, current)}
            </div>
            <span className="seller-profile-changes__arrow" aria-hidden>
              →
            </span>
            <div className="seller-profile-changes__side seller-profile-changes__side--next">
              <span className="seller-profile-changes__side-label">Новое</span>
              {renderValue(field, pending)}
            </div>
            <span className="seller-profile-changes__actions">
              <Tooltip label="Одобрить">
                <ActionIcon
                  variant="light"
                  color="green"
                  size="sm"
                  radius="md"
                  aria-label={`Одобрить: ${FIELD_LABELS[field]}`}
                  disabled={busy}
                  loading={approveMut.isPending && approveMut.variables === field}
                  onClick={() => approveMut.mutate(field)}
                >
                  <IoCheckmark size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Отклонить">
                <ActionIcon
                  variant="light"
                  color="red"
                  size="sm"
                  radius="md"
                  aria-label={`Отклонить: ${FIELD_LABELS[field]}`}
                  disabled={busy}
                  onClick={() => {
                    setRejectReason('');
                    setRejectField(field);
                  }}
                >
                  <IoClose size={18} />
                </ActionIcon>
              </Tooltip>
            </span>
          </li>
        ))}
      </ul>

      <Modal
        isOpen={rejectField != null}
        onClose={() => setRejectField(null)}
        title={rejectField ? `Отклонить: ${FIELD_LABELS[rejectField].toLowerCase()}` : 'Отклонить'}
        maxWidth={440}
      >
        <div className="seller-detail-block-modal">
          <p className="seller-detail-block-modal__hint">
            На витрине останется прежнее значение, причина придёт продавцу в уведомления.
          </p>
          <Textarea
            label="Причина для продавца"
            placeholder="Например: название содержит контактные данные"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={3}
            autosize
          />
          <div className="seller-detail-block-modal__actions">
            <Button variant="secondary" onClick={() => setRejectField(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={rejectMut.isPending}
              disabled={!rejectReason.trim()}
              onClick={() => {
                if (!rejectField) return;
                rejectMut.mutate({ field: rejectField, reason: rejectReason.trim() });
              }}
            >
              Отклонить
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
